import { css } from "@emotion/css";
import { Button, Callout, Divider } from "@octopusdeploy/design-system-components";
import { useInlineStatusQuery } from "@octopusdeploy/octopus-react-client";
import type { ArgoCDGatewayHealthStatus } from "@octopusdeploy/octopus-server-client";
import { ScriptingLanguage } from "@octopusdeploy/octopus-server-client";
import { noOp } from "@octopusdeploy/utilities";
import React, { useEffect, useState } from "react";
import type { ArgoCDInstanceConfiguration } from "~/areas/infrastructure/components/ArgoCDInstances/ArgoCDInstanceConfiguration";
import { GatewayDiscoveryProgress } from "~/areas/infrastructure/components/ArgoCDInstances/DialogViews/components/GatewayDiscoveryProgress";
import { generateHelmCommand } from "~/areas/infrastructure/components/ArgoCDInstances/createArgoCDGatewayHelmCommand";
import { useArgoCDInstanceHelmCommandGeneratedDispatcher } from "~/areas/infrastructure/components/ArgoCDInstances/useArgoCDInstanceHelmCommandGeneratedDispatcher";
import type { UseBearerTokenReturnValue } from "~/areas/infrastructure/components/ArgoCDInstances/useBearerToken";
import { contentContainerStyles } from "~/areas/infrastructure/components/MachineSettings/Endpoints/KubernetesAgent/Styles";
import type { Language, TextFormat } from "~/components/CodeEditor/CodeEditor";
import { CodeEditor } from "~/components/CodeEditor/CodeEditor";
import TimeFromNowLabel from "~/components/TimeLabels/TimeFromNowLabel";
type InstallationViewProps = {
    configuration: ArgoCDInstanceConfiguration;
    onGatewayFound: (gateway: FoundGatewayInstance) => void;
    bearerToken: Exclude<UseBearerTokenReturnValue, "refresh">;
};
export type FoundGatewayInstance = {
    InstanceId: string;
    GatewayId: string;
    SpaceId: string;
};
export const InstallationView = (props: InstallationViewProps) => {
    const [hideSearch, setHideSearch] = useState(true);
    const [isFound, setIsFound] = useState(false);
    const [hideHelmCommand, setHideHelmCommand] = useState(false);
    const [gatewayHealthStatus, setGatewayHealthStatus] = useState<ArgoCDGatewayHealthStatus>("Unknown");
    const dispatchArgoInstallationAnalyticsEvent = useArgoCDInstanceHelmCommandGeneratedDispatcher();
    useEffect(() => {
        // Set a timeout to show the component after 2000 milliseconds (2s)
        const timeoutId = setTimeout(() => {
            setHideSearch(false);
        }, 2000);
        dispatchArgoInstallationAnalyticsEvent("Argo CD Gateway Helm command generated", {
            gatewayName: props.configuration.name,
            spaceId: props.configuration.spaceId,
        });
        // Clean up the timeout when the component unmounts
        return () => clearTimeout(timeoutId);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [props.configuration.name, props.configuration.spaceId]);
    useInlineStatusQuery(async (repo) => {
        if (isFound && gatewayHealthStatus !== "Unknown") {
            return;
        }
        const instanceSummaries = await repo.ArgoCDInstances.list({ name: props.configuration.name });
        //we should only have one or 0
        const instanceSummary = instanceSummaries.Resources[0];
        if (instanceSummary) {
            setIsFound(true);
            setHideSearch(false);
            setHideHelmCommand(true);
            setGatewayHealthStatus(instanceSummary.HealthStatus);
            props.onGatewayFound({
                InstanceId: instanceSummary.Id,
                ...instanceSummary,
            });
        }
        return instanceSummaries;
    }, [gatewayHealthStatus, isFound, props], "Find gateway", {
        //if this is not found and the health check has not run, we want to poll every 2s
        refetchIntervalInMs: isFound && gatewayHealthStatus !== "Unknown" ? undefined : 2000,
    });
    const [selectedLanguage, setSelectedLanguage] = useState<ScriptingLanguage | Language | TextFormat>(ScriptingLanguage.Bash);
    const helmCommand = generateHelmCommand(props.configuration, props.bearerToken.accessToken === null ? undefined : props.bearerToken.accessToken, selectedLanguage);
    const tokenExpiredAndHaveGivenUp = !isFound && props.bearerToken.hasExpired;
    const expiryTimeInfo = !isFound && props.bearerToken.expiry && (<>
            Your token expires{" "}
            <b>
                <TimeFromNowLabel time={props.bearerToken.expiry.toISOString()}/>
            </b>
            .
        </>);
    const helmCommandLineCount = helmCommand.split(/\r\n|\r|\n/).length;
    //adjust the height of the code editor based on the number of lines in the helm command
    const codeEditorContainer = css({
        // 1.25rem per line (line height) and roughly 3rem of padding/margins/scrollbars
        minHeight: `calc((${helmCommandLineCount} * 1.25rem) + 3rem)`,
    });
    return (<div className={contentContainerStyles}>
            {!tokenExpiredAndHaveGivenUp && (<>
                    <p>We've generated a custom Helm command for you which includes a short-lived bearer token for Octopus Server. {expiryTimeInfo}</p>
                    <p>Copy and run this Helm command in a terminal connected to your Kubernetes cluster.</p>
                </>)}
            {tokenExpiredAndHaveGivenUp && (<Callout title={"Your bearer token has expired"} type={"danger"}>
                    <p>Regenerate the token and copy the updated Helm command.</p>
                </Callout>)}
            {isFound && (<div className="helmButtonContainer">
                    <Button importance="tertiary" label={hideHelmCommand ? "Show Helm command" : "Hide Helm command"} onClick={() => setHideHelmCommand(!hideHelmCommand)}></Button>
                </div>)}
            {!hideHelmCommand && (<>
                    <CodeEditor value={helmCommand} readOnly lineWrapping={false} showCopyButton showLineNumbers={false} language={selectedLanguage} allowFullScreen containerClassName={codeEditorContainer} settingsOverride={{ theme: "default", wordWrap: false }} onChange={noOp} scriptingLanguageSelectorOptions={{
                supportedLanguages: [ScriptingLanguage.PowerShell, ScriptingLanguage.Bash],
                onScriptingLanguageChanged: (syntax: ScriptingLanguage | Language | TextFormat) => setSelectedLanguage(syntax),
            }}/>
                </>)}
            {isFound && <Divider />}
            {!hideSearch && !tokenExpiredAndHaveGivenUp && <GatewayDiscoveryProgress name={props.configuration.name} isFound={isFound} bearerTokenExpired={props.bearerToken.hasExpired} gatewayHealthStatus={gatewayHealthStatus}/>}
        </div>);
};
